import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import * as XLSX from "xlsx";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

import RTMFilterBar from "./RTMFilterBar";
import UIRTMButton from "../UIRTMButton";
import ExecutionsGate from "../executionRTM/ExecutionsGate";

import "./RTMMappings.css";

// Modular Components
import { getTemplateFields } from "../templatesLoader";
import useOutsideClick from "../useOutsideClick";

import MappingCanvas from "./MappingCanvas";
import MappingSidebar from "./MappingSidebar";
import RTMSetupStepper from "./RTMSetupStepper";
import RTMEmptyState from "./RTMEmptyState";
import { useSetupStatus } from "./useSetupStatus";

import { useMapping } from "./useMapping";

const ALM_LABELS = {
  jira: "Jira",
  ado: "Azure DevOps (ADO)",
};
const TEMPLATE_REQUIREMENTS = {
  storyRtm: ["project", "issueTypes"],
  testRtm: ["project", "issueTypes"],
  defectAnalytics: ["project", "issueTypes"],
  executionRtm: ["project", "fixVersions"], // your change
};

const RTMMappings = () => {
  const [uiStatus, setUiStatus] = useState({
    loading: true,
    fetchingMeta: false,
    checkingMappings: false,
    downloadLoading: false,
    saveLoading: false,
    uploadLoading: false,
  });

  const [almProviders, setAlmProviders] = useState({});
  const [almInfo, setAlmInfo] = useState({
    status: "idle",
    activeName: "",
    error: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [userInfo, setUserInfo] = useState({ firstName: "", fullName: "" });

  const [issueTypeOpen, setIssueTypeOpen] = useState(false);
  const [fixVersionOpen, setFixVersionOpen] = useState(false);

  const issueTypeRef = useRef(null);
  const fixVersionRef = useRef(null);

  const [mappings, setMappings] = useState({
    alm: "",
    template: "",
    project: null,
    issueTypes: [],
    fixVersions: [],
  });
  const [options, setOptions] = useState({
    templates: [
      { id: "storyRtm", label: "User Story RTM" },
      { id: "testRtm", label: "Test RTM" },
      { id: "executionRtm", label: "Test Execution RTM" },
      { id: "defectAnalytics", label: "Defect Analytics" },
    ],
    alms: [],
    projects: [],
    issueTypes: [],
    fixVersions: [],
    priorities: [],
    labels: [],
    jiraFields: [],
  });
  const [selectedJira, setSelectedJira] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    priority: [],
    labels: [],
  });
  const [existingMappings, setExistingMappings] = useState([]);

  const isMappingReady = React.useMemo(
    () =>
      mappings.template && mappings.project && mappings.issueTypes.length > 0,
    [mappings.template, mappings.project, mappings.issueTypes],
  );

  const isSelectionComplete = React.useMemo(() => {
    if (!mappings.template) return false;

    const required = TEMPLATE_REQUIREMENTS[mappings.template] || [];

    if (required.includes("project") && !mappings.project) return false;
    if (required.includes("issueTypes") && !mappings.issueTypes.length)
      return false;
    if (required.includes("fixVersions") && !mappings.fixVersions.length)
      return false;

    return true;
  }, [
    mappings.template,
    mappings.project,
    mappings.issueTypes,
    mappings.fixVersions,
  ]);

  const [templateOpen, setTemplateOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef(null);

  const templateRef = useRef(null);
  const projectRef = useRef(null);

  const LINKED_ISSUE_KEY_COL = "Linked Issue Key";
  const LINKED_ISSUE_TYPE_COL = "Linked Issue Type";

  useOutsideClick({
    refs: [issueTypeRef, fixVersionRef, templateRef, projectRef],
    enabled: issueTypeOpen || fixVersionOpen || templateOpen || projectOpen,
    handler: () => {
      setIssueTypeOpen(false);
      setFixVersionOpen(false);
      setTemplateOpen(false);
      setProjectOpen(false);
    },
  });

  useOutsideClick({
    refs: [filtersRef],
    enabled: filtersOpen,
    handler: () => setFiltersOpen(false),
  });

  const {
    getStepStatus: setupGetStepStatus,
    isSelectionComplete: setupSelectionComplete,
  } = useSetupStatus(mappings, almInfo.status);

  const mappingState = useMapping();

  //const templateFields = getTemplateFields(mappings.template);
  const templateFields = React.useMemo(
    () => getTemplateFields(mappings.template),
    [mappings.template],
  );

  const systemFields = [
    {
      id: "issueKey",
      label: "Issue Key",
      type: "system",
      required: true,
      readOnly: true,
    },
  ];

  const jiraFields = React.useMemo(() => {
    if (!options.jiraFields.length) return systemFields;

    return [...systemFields, ...options.jiraFields];
  }, [options.jiraFields]);

  useEffect(() => {
    fetchInitialConfig();
    const userDetailsStr = sessionStorage.getItem("UserDetails");
    if (userDetailsStr) {
      const userDetails = JSON.parse(userDetailsStr);
      const firstName = userDetails.data?.User?.FirstName || "";
      const lastName = userDetails.data?.User?.LastName || "";
      setUserInfo({
        firstName,
        fullName: `${firstName} ${lastName}`.trim() || firstName,
      });
    }
  }, []);

  useEffect(() => {
    if (
      mappings.template &&
      mappings.project &&
      mappings.issueTypes.length > 0
    ) {
      fetchExistingMappings(mappings);
    }
  }, [mappings.template, mappings.project, mappings.issueTypes]);

  const buildJql = () => {
    const clauses = [`project = ${mappings.project.key}`];

    if (mappings.issueTypes.length) {
      clauses.push(
        `issuetype IN (${mappings.issueTypes.map((v) => `"${v.name}"`).join(",")})`,
      );
    }
    if (filters.startDate) clauses.push(`created >= "${filters.startDate}"`);

    if (filters.endDate) clauses.push(`created <= "${filters.endDate}"`);

    if (mappings.fixVersions.length) {
      clauses.push(
        `fixVersion IN (${mappings.fixVersions
          .map((v) => `"${v.name}"`)
          .join(",")})`,
      );
    }

    if (filters.labels.length) {
      clauses.push(
        `labels IN (${filters.labels.map((l) => `"${l}"`).join(",")})`,
      );
    }

    if (filters.priority.length) {
      clauses.push(
        `priority IN (${filters.priority.map((p) => `"${p}"`).join(",")})`,
      );
    }

    return clauses.join(" AND ");
  };

  const resetProjectDependentFields = () => {
    setMappings((prev) => ({
      ...prev,
      project: null,
      issueTypes: [],
      fixVersions: [],
    }));

    setFilters({
      startDate: "",
      endDate: "",
      priority: [],
      labels: [],
    });

    setOptions((prev) => ({
      ...prev,
      projects: prev.projects,
      issueTypes: [],
      fixVersions: [],
      priorities: [],
      labels: [],
    }));

    setExistingMappings([]);
    mappingState.setMappings([]);
  };

  const resetTemplateDependentFields = () => {
    setMappings((prev) => ({
      ...prev,
      template: prev.template,
      project: null,
      issueTypes: [],
      fixVersions: [],
    }));

    resetProjectDependentFields();
  };

  const fetchInitialConfig = async () => {
    setUiStatus((prev) => ({ ...prev, loading: true }));
    const projectIndex = sessionStorage.getItem("selectedProjectIndex");
    const siteIndex = sessionStorage.getItem("selectedSiteIndex");
    const userDetails = JSON.parse(sessionStorage.getItem("UserDetails"));
    const pId =
      userDetails?.data?.User?.rights?.Projects?.[projectIndex]?.projectId ||
      "";
    const sId =
      userDetails?.data?.User?.rights?.Projects?.[projectIndex]?.sites?.[
        siteIndex
      ]?.siteid || "";
    if (!pId || !sId) {
      setUiStatus((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      const headerData = {
        Authorization: sessionStorage.getItem("authToken") || "",
        "ch-subscription-key":
          userDetails?.data?.User?.["ch-subscription-key"] || "",
      };
      const result = await axios.get(
        `${window.configs.BASE_API_URL}${window.configs.ADMIN_SERVICE_PROJECT_URL}/${pId}/${sId}`,
        { headers: headerData },
      );
      if (result.data.status === "success") {
        const rtm = result.data.info?.rtm_settings;
        if (!rtm) {
          setAlmInfo((prev) => ({
            ...prev,
            status: "invalid",
            error: "Add ALM credentials in Settings",
          }));
          toast.info("Add ALM credentials in Settings"); // direct call
          return;
        }
        const providers = rtm?.almProviders || {};

        if (!Object.keys(providers).length) {
          setAlmInfo((prev) => ({
            ...prev,
            status: "invalid",
            error: "Add ALM credentials in Settings",
          }));
          toast.info("Add ALM credentials in Settings"); //  direct call
          return;
        }
        const activeALMKey = rtm?.activeALM || "";
        setAlmProviders(providers);
        setOptions((prev) => ({
          ...prev,
          alms: Object.keys(providers).map((key) => ALM_LABELS[key] || key),
        }));

        if (activeALMKey && providers[activeALMKey]) {
          const label = ALM_LABELS[activeALMKey] || activeALMKey;

          handleAlmSelection(label, true, providers);
        } else {
          setAlmInfo((prev) => ({
            ...prev,
            status: "invalid",
            error: "Add ALM credentials in Settings",
          }));
          toast.info("Add ALM credentials in Settings");
        }
      }
    } catch (error) {
      toast.error("Failed to load configuration");
    } finally {
      setUiStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleAlmSelection = useCallback(
    async (selectedAlmLabel, silent = false, providersOverride = null) => {
      if (!selectedAlmLabel) {
        setAlmInfo((prev) => ({ ...prev, status: "idle" }));
        setMappings((prev) => ({
          ...prev,
          alm: "",
          project: null,
          issueTypes: [],
          fixVersions: [],
        }));
        return;
      }
      const providers = providersOverride || almProviders;
      const almKey =
        Object.keys(ALM_LABELS).find(
          (key) => ALM_LABELS[key] === selectedAlmLabel,
        ) || selectedAlmLabel.toLowerCase();
      const config = providers[almKey];
      setMappings((prev) => ({
        ...prev,
        alm: selectedAlmLabel,
        project: null,
        issueTypes: [],
        fixVersions: [],
      }));

      const hasCreds =
        almKey === "jira"
          ? !!(config?.domain && config?.token && config?.email)
          : !!(config?.organization && config?.project && config?.pat);
      if (!hasCreds) {
        setAlmInfo((prev) => ({
          ...prev,
          status: "invalid",
          error: "Add ALM credentials in Settings",
        }));
        toast.info("Add ALM credentials in Settings");

        if (!silent) toast.warn(`No credentials for ${selectedAlmLabel}.`);
        return;
      }

      setAlmInfo((prev) => ({ ...prev, status: "checking" }));
      try {
        const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_JIRA_HANDLER_URL}`;
        const response = await axios.post(apiUrl, {
          action: "test_connection",
          credentials: { ...config },
        });
        if (response?.data?.status === "success") {
          setAlmInfo({
            status: "valid",
            activeName: selectedAlmLabel,
            error: "",
          });
          //if (!silent) toast.success(`${selectedAlmLabel} connected!`);
          fetchProjects(config);
        } else throw new Error();
      } catch (error) {
        setAlmInfo({
          status: "invalid",
          activeName: selectedAlmLabel,
          error: "Invalid credentials",
        });
        toast.info("Invalid credentials");
        //if (!silent) toast.error(`Connection failed.`);
      }
    },
    [almProviders],
  );

  const fetchProjects = async (config) => {
    try {
      const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_JIRA_HANDLER_URL}`;
      const response = await axios.post(apiUrl, {
        action: "get_projects",
        credentials: { ...config },
      });
      if (response?.data?.status === "success")
        setOptions((prev) => ({
          ...prev,
          projects: (response.data?.info || []).map((p) => ({
            id: p.id,
            key: p.key,
            name: p.name,
          })),
        }));
    } catch (error) {
      toast.error("Failed to fetch projects.");
    }
  };

  const mapFieldType = (schema = {}) => {
    switch (schema?.type) {
      case "string":
        return "text";
      case "number":
        return "number";
      case "date":
        return "date";
      case "datetime":
        return "datetime";
      case "array":
        return "multi";
      case "user":
        return "user";
      default:
        return schema?.type || "text";
    }
  };

  const fetchProjectMetadata = async (project) => {
    if (!project) return;
    const almKey =
      Object.keys(ALM_LABELS).find((key) => ALM_LABELS[key] === mappings.alm) ||
      mappings.alm.toLowerCase();
    const config = almProviders[almKey];
    setUiStatus((prev) => ({ ...prev, fetchingMeta: true }));
    const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_JIRA_HANDLER_URL}`;
    const basePayload = {
      params: { project_key: project.key },
      credentials: { ...config },
    };

    try {
      const metaActions = [
        {
          action: "get_project_issue_types",
          key: "issueTypes",
          map: (d) =>
            (d.info?.issueTypes || d.info || []).map((it) => ({
              id: it.id,
              name: it.name,
              iconUrl: it.iconUrl,
            })),
        },
        {
          action: "get_project_versions",
          key: "fixVersions",
          map: (d) => (d.info || []).map((v) => ({ id: v.id, name: v.name })),
        },
        {
          action: "get_labels",
          key: "labels",
          map: (d) => d.info?.values || d.info || [],
        },
        {
          action: "get_priorities",
          key: "priorities",
          map: (d) =>
            (d.info || []).map((v) => ({
              id: v.id,
              name: v.name,
              iconUrl: v.iconUrl,
            })),
        },
        {
          action: "get_project_fields",
          key: "jiraFields",
          map: (d) =>
            (d.info || []).map((f) => ({
              id: f.id,
              label: f.name,
              type: mapFieldType(f.schema),
              custom: f.custom,
            })),
        },
      ];
      const results = await Promise.allSettled(
        metaActions.map((a) =>
          axios.post(apiUrl, { ...basePayload, action: a.action }),
        ),
      );
      const newOptions = { ...options };
      results.forEach((res, i) => {
        if (res.status === "fulfilled" && res.value.data?.status === "success")
          newOptions[metaActions[i].key] = metaActions[i].map(res.value.data);
      });
      setOptions(newOptions);
    } catch (e) {
      console.error(e);
    } finally {
      setUiStatus((prev) => ({ ...prev, fetchingMeta: false }));
    }
  };

  const handleAutocompleteChange = (field, value) => {
    if (field === "template") {
      setMappings((prev) => ({
        ...prev,
        template: value,
        project: null,
        issueTypes: [],
        fixVersions: [],
      }));

      resetProjectDependentFields();
      return;
    }

    if (field === "project") {
      setMappings((prev) => ({
        ...prev,
        project: value,
        issueTypes: [],
        fixVersions: [],
      }));

      // Reset dependent data
      setFilters({
        startDate: "",
        endDate: "",
        priority: [],
        labels: [],
      });

      mappingState.setMappings([]);
      setExistingMappings([]);

      fetchProjectMetadata(value);
      return;
    }

    // Default behavior
    setMappings((prev) => ({ ...prev, [field]: value }));
  };
  const normalize = (arr) => arr.map((i) => i.toLowerCase().trim()).sort();

  const isSameArray = (a, b) =>
    a.length === b.length && a.every((val, i) => val === b[i]);

  const fetchExistingMappings = async (currentMappings) => {
    setUiStatus((prev) => ({ ...prev, checkingMappings: true }));

    try {
      const pId = JSON.parse(sessionStorage.getItem("UserDetails"))?.data?.User
        ?.rights?.Projects?.[sessionStorage.getItem("selectedProjectIndex")]
        ?.projectId;

      const sId = JSON.parse(sessionStorage.getItem("UserDetails"))?.data?.User
        ?.rights?.Projects?.[sessionStorage.getItem("selectedProjectIndex")]
        ?.sites?.[sessionStorage.getItem("selectedSiteIndex")]?.siteid;

      const headerData = {
        Authorization: sessionStorage.getItem("authToken"),
        "ch-subscription-key": JSON.parse(sessionStorage.getItem("UserDetails"))
          ?.data?.User?.["ch-subscription-key"],
      };

      const res = await axios.get(
        `${window.configs.BASE_API_URL}${window.configs.ADMIN_SERVICE_PROJECT_URL}/${pId}/${sId}`,
        { headers: headerData },
      );

      const allMappings = res.data.info?.rtm_mappings || [];

      const matched = allMappings.find((m) => {
        const currentIssueTypes = normalize(
          currentMappings.issueTypes.map((it) => it.name),
        );

        const savedIssueTypes = normalize(m.issueTypes);

        return (
          m.projectKey === currentMappings.project.key &&
          m.mappingType === currentMappings.template &&
          isSameArray(currentIssueTypes, savedIssueTypes)
        );
      });

      if (matched) {
        setExistingMappings(matched.mappings || []);
        mappingState.setMappings(matched.mappings || []);
      } else {
        setExistingMappings([]);
        mappingState.setMappings([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setUiStatus((prev) => ({ ...prev, checkingMappings: false }));
    }
  };

  const handleSaveMapping = useCallback(async () => {
    if (
      !mappings.project ||
      !mappings.issueTypes.length ||
      !mappingState.mappings.length
    ) {
      toast.error("Complete all required fields.");
      return;
    }
    const mappingId = `${mappings.project.key}-${mappings.template.replace(/\s+/g, "")}-${mappings.issueTypes
      .map((i) => i.name)
      .join(",")}`;
    const newMapping = {
      id: mappingId.toLowerCase(),
      projectKey: mappings.project.key,
      mappingType: mappings.template,
      issueTypes: mappings.issueTypes.map((it) => it.name.toLowerCase()),
      fixVersions: mappings.fixVersions.map((v) => v.name),
      filters,
      mappings: mappingState.mappings.map((m) => ({
        rowId: m.rowId,
        jiraId: m.jiraId,
        jiraLabel: m.jiraLabel,
        templateId: m.templateId,
        templateLabel: m.templateLabel,
      })),
      savedAt: new Date().toISOString(),
    };
    const pId = JSON.parse(sessionStorage.getItem("UserDetails"))?.data?.User
      ?.rights?.Projects?.[sessionStorage.getItem("selectedProjectIndex")]
      ?.projectId;
    const sId = JSON.parse(sessionStorage.getItem("UserDetails"))?.data?.User
      ?.rights?.Projects?.[sessionStorage.getItem("selectedProjectIndex")]
      ?.sites?.[sessionStorage.getItem("selectedSiteIndex")]?.siteid;

    try {
      setUiStatus((prev) => ({ ...prev, saveLoading: true }));
      const headerData = {
        Authorization: sessionStorage.getItem("authToken"),
        "ch-subscription-key": JSON.parse(sessionStorage.getItem("UserDetails"))
          ?.data?.User?.["ch-subscription-key"],
      };
      const current = await axios.get(
        `${window.configs.BASE_API_URL}${window.configs.ADMIN_SERVICE_PROJECT_URL}/${pId}/${sId}`,
        { headers: headerData },
      );
      let existing = current.data.info?.rtm_settings?.mappings || [];

      const idx = existing.findIndex((m) => m.id === mappingId);
      if (idx > -1) existing[idx] = newMapping;
      else existing.push(newMapping);

      await axios.patch(
        `${window.configs.BASE_API_URL}${window.configs.ADMIN_SERVICE_PROJECT_URL}/${pId}/${sId}/rtmMappings`,
        {
          rtm_mappings: {
            id: existing[0].id,
            projectKey: existing[0].projectKey,
            issueTypes: existing[0].issueTypes,
            mappingType: existing[0].mappingType,
            mappings: existing[0].mappings,
          },
        },
        { headers: headerData },
      );
      toast.success("Mapping saved successfully!");
    } catch (e) {
      toast.error("Error saving mapping.");
    } finally {
      setUiStatus((prev) => ({ ...prev, saveLoading: false }));
    }
  }, [mappings, mappingState.mappings, filters]);

  const getCredentials = useCallback(() => {
    const almKey =
      Object.keys(ALM_LABELS).find((key) => ALM_LABELS[key] === mappings.alm) ||
      mappings.alm.toLowerCase();

    const config = almProviders[almKey];

    if (!config) return null;

    const tmType = config?.testManagement?.activeTM;
    const tmConfig = config?.testManagement?.tmConfigs?.[tmType] || {};

    return {
      domain: config.domain,
      email: config.email,
      token: config.token,

      // Zephyr specific
      accessKey: tmConfig.accessKey,
      secretKey: tmConfig.secretKey,
      tmType,
    };
  }, [mappings.alm, almProviders]);

  const formatDate = (d) => (d ? new Date(d).toISOString() : "");

  const fetchAndProcessIssues = async () => {
    const credentials = getCredentials();
    const jiraFieldsArr = Array.from(
      new Set(
        mappingState.mappings
          .map((m) => m.jiraId)
          .filter((id) => id && id !== "issueKey")
          .concat("issuelinks"),
      ),
    );

    const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_JIRA_HANDLER_URL}`;

    const response = await axios.post(apiUrl, {
      action: "get_all_issues",
      credentials,
      params: {
        fields: jiraFieldsArr,
        jql: buildJql(),
      },
    });

    if (response?.data?.status !== "success") {
      throw new Error("Failed to fetch issues");
    }

    const issues = [];

    response.data.info.forEach((issue) => {
      const baseRow = {};

      mappingState.mappings.forEach((m) => {
        if (m.jiraId === "issueKey") {
          baseRow[m.templateLabel] = issue.key;
          return;
        }

        if (m.jiraId === "issuelinks") return;

        const value = issue.fields[m.jiraId];
        if (["created", "updated", "resolved"].includes(m.jiraId)) {
          baseRow[m.templateLabel] = formatDate(value);
          return;
        }

        if (Array.isArray(value)) {
          baseRow[m.templateLabel] = value
            .map((v) => v.name || v.value || v)
            .join(", ");
        } else if (value?.name) {
          baseRow[m.templateLabel] = value.name;
        } else if (value?.displayName) {
          baseRow[m.templateLabel] = value.displayName;
        } else {
          baseRow[m.templateLabel] = value ?? "";
        }
      });

      const links = issue.fields.issuelinks || [];
      const linkedIssues = links
        .map((l) => l.outwardIssue || l.inwardIssue)
        .filter(Boolean)
        .map((li) => ({
          key: li.key,
          type: li.fields?.issuetype?.name || "",
        }));

      if (!linkedIssues.length) {
        issues.push({
          ...baseRow,
          [LINKED_ISSUE_KEY_COL]: "",
          [LINKED_ISSUE_TYPE_COL]: "",
        });
        return;
      }
      linkedIssues.forEach((li) => {
        issues.push({
          ...baseRow,
          [LINKED_ISSUE_KEY_COL]: li.key,
          [LINKED_ISSUE_TYPE_COL]: li.type,
        });
      });
    });

    return issues;
  };

  const generateWorkbook = (issues) => {
    const orderedTemplateFields = [
      ...templateFields.filter((f) => f.mandatory),
      ...templateFields.filter((f) => !f.mandatory),
    ];
    const headers = orderedTemplateFields.map((f) => f.label);
    const hasLinkedIssues = orderedTemplateFields.some(
      (f) => f.id === "linkedIssuesId",
    );
    if (hasLinkedIssues) headers.push("Linked Issue Type");

    const rows = issues.map((issue) => {
      const row = {};
      orderedTemplateFields.forEach((field) => {
        if (field.id === "linkedIssuesId") {
          row[field.label] = issue[LINKED_ISSUE_KEY_COL] ?? "";
        } else {
          row[field.label] = issue[field.label] ?? "";
        }
      });
      if (hasLinkedIssues) {
        row["Linked Issue Type"] = issue[LINKED_ISSUE_TYPE_COL] ?? "";
      }
      return row;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    XLSX.utils.sheet_add_json(worksheet, rows, {
      skipHeader: true,
      origin: "A2",
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, mappings.template);

    return workbook;
  };
  const getFormattedFileName = (suffix = "") => {
    const issueTypes = mappings.issueTypes
      .map((i) => i.name)
      .join(",")
      .toLowerCase();

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

    return `${mappings.project.key}-${mappings.template}-${issueTypes}-report-${suffix || timestamp}.xlsx`;
  };

  const handleGenerateReport = useCallback(async () => {
    try {
      setUiStatus((prev) => ({ ...prev, downloadLoading: true }));
      const issues = await fetchAndProcessIssues();
      const workbook = generateWorkbook(issues);

      XLSX.writeFile(workbook, getFormattedFileName());
      toast.success("Report downloaded successfully");
    } catch (err) {
      console.error(err);
      toast.error("Report generation failed");
    } finally {
      setUiStatus((prev) => ({ ...prev, downloadLoading: false }));
    }
  }, [fetchAndProcessIssues, generateWorkbook, getFormattedFileName]);

  const uploadReport = async (formData) => {
    const apiUrl = `${window.configs.BASE_API_URL}${window.configs.AIQA_UPLOAD_RTM_URL}`; // adjust

    return axios.post(apiUrl, formData, {
      headers: {
        Authorization: sessionStorage.getItem("authToken"),
        "ch-subscription-key": JSON.parse(sessionStorage.getItem("UserDetails"))
          ?.data?.User?.["ch-subscription-key"],
      },
    });
  };

  const handleUploadReport = useCallback(
    async (target) => {
      try {
        setUiStatus((prev) => ({ ...prev, uploadLoading: true }));

        const issues = await fetchAndProcessIssues();
        const workbook = generateWorkbook(issues);

        const fileName = getFormattedFileName();

        const wbArray = XLSX.write(workbook, {
          type: "array",
          bookType: "xlsx",
        });

        const blob = new Blob([wbArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create File object
        const file = new File([blob], fileName, { type: blob.type });

        const formData = new FormData();
        formData.append("file", file);
        formData.append("fileName", fileName);
        formData.append("projectKey", mappings.project.key);
        formData.append("projectName", mappings.project.name);
        formData.append("target", target);
        formData.append("templateType", mappings.template);

        await uploadReport(formData);
        toast.success("Upload successful");
      } catch (err) {
        console.error(err);
        toast.error("Upload failed");
      } finally {
        setUiStatus((prev) => ({ ...prev, uploadLoading: false }));
      }
    },
    [
      fetchAndProcessIssues,
      generateWorkbook,
      getFormattedFileName,
      mappings.template,
      mappings.project,
    ],
  );

  const getStepStatus = useCallback(
    (step) => {
      const required = TEMPLATE_REQUIREMENTS[mappings.template] || [];

      if (step === "template") {
        return mappings.template ? "completed" : "active";
      }

      if (step === "project") {
        return !mappings.template
          ? "pending"
          : mappings.project && almInfo.status === "valid"
            ? "completed"
            : "active";
      }

      if (step === "issueType") {
        if (!required.includes("issueTypes")) return "hidden"; // skip
        return !mappings.project || almInfo.status !== "valid"
          ? "pending"
          : mappings.issueTypes.length > 0
            ? "completed"
            : "active";
      }

      if (step === "fixVersion") {
        if (!required.includes("fixVersions")) return "hidden"; // skip
        return !mappings.project
          ? "pending"
          : mappings.fixVersions.length > 0
            ? "completed"
            : "active";
      }

      return "pending";
    },
    [
      mappings.template,
      mappings.project,
      mappings.issueTypes,
      mappings.fixVersions,
      almInfo.status,
    ],
  );

  return (
    <>
      <div className="card custcard">
        <div className="cord-body">
          <div
            className="testHeading"
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "0 15px",
            }}
          >
            <label
              className="headermsg1"
              style={{ margin: 0, paddingLeft: "40px" }}
            >
              RTM Mappings
            </label>
            {uiStatus.loading ? (
              <Box className="rtm-loader-container">
                <CircularProgress size={24} />
              </Box>
            ) : (
              <div className="header-status-container">
                <p
                  className={`rtm-status ${
                    almInfo.status === "valid"
                      ? "valid"
                      : almInfo.status === "invalid"
                        ? "invalid"
                        : "checking"
                  }`}
                >
                  {almInfo.status === "valid"
                    ? `${almInfo.activeName} Connected`
                    : almInfo.status === "invalid"
                      ? `${almInfo.activeName || "ALM"} Not Connected`
                      : almInfo.status === "checking"
                        ? `Checking ${almInfo.activeName}...`
                        : "Not connected to any ALM"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card custcard">
        <ToastContainer position="bottom-right" autoClose={3000} />
        <div className="cord-body mt-3">
          <Box className="rtm-mapping-container">
            <RTMFilterBar
              mappings={mappings}
              options={options}
              filters={filters}
              templateOpen={templateOpen}
              setTemplateOpen={setTemplateOpen}
              templateRef={templateRef}
              projectOpen={projectOpen}
              setProjectOpen={setProjectOpen}
              projectRef={projectRef}
              issueTypeOpen={issueTypeOpen}
              setIssueTypeOpen={setIssueTypeOpen}
              issueTypeRef={issueTypeRef}
              fixVersionOpen={fixVersionOpen}
              setFixVersionOpen={setFixVersionOpen}
              fixVersionRef={fixVersionRef}
              filtersOpen={filtersOpen}
              setFiltersOpen={setFiltersOpen}
              filtersRef={filtersRef}
              handleChange={handleAutocompleteChange}
              setFilters={setFilters}
            />

            {!setupSelectionComplete && (
              <Box sx={{ mt: 2 }}>
                <RTMSetupStepper
                  mappings={mappings}
                  getStepStatus={setupGetStepStatus}
                />
              </Box>
            )}

            {isSelectionComplete && mappings.template === "executionRtm" && (
              <ExecutionsGate
                testAppCredentials={getCredentials()}
                testAppName="zephyrSquad" // or dynamic later
                projectId={mappings.project?.id}
                projectKey={mappings.project?.key}
                projectName={mappings.project?.name}
                versionId={mappings.fixVersions[0]?.id}
                versionName={mappings.fixVersions[0]?.name}
              />
            )}

            {isSelectionComplete && mappings.template !== "executionRtm" && (
              <>
                {uiStatus.checkingMappings ? (
                  // Loading
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : existingMappings.length === 0 ? (
                  // No mappings found
                  <Box sx={{ width: "100%" }}>
                    <RTMEmptyState
                      onCreateMapping={() =>
                        setExistingMappings([{ temp: true }])
                      }
                    />
                  </Box>
                ) : (
                  //  Show mapping canvas
                  <Box className="rtm-mapping-canvas">
                    <MappingCanvas
                      jiraFields={jiraFields}
                      templateFields={templateFields}
                      mappingState={mappingState}
                    />

                    <MappingSidebar
                      mappingState={mappingState}
                      templateFields={templateFields}
                      templateType={mappings.template}
                      onSave={handleSaveMapping}
                      onGenerate={handleGenerateReport}
                      onUpload={handleUploadReport}
                      onFetchIssues={fetchAndProcessIssues}
                      downloadLoading={uiStatus.downloadLoading}
                      saveLoading={uiStatus.saveLoading}
                      uploadLoading={uiStatus.uploadLoading}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </div>
      </div>
    </>
  );
};

export default RTMMappings;
